export type InternalPermProp = keyof server.dto.InternalEnvironmentPermissions;
export type ExternalPermProp = keyof server.dto.ExternalEnvironmentPermissions;

interface Permission<T extends server.dto.InternalEnvironmentPermissions | server.dto.ExternalEnvironmentPermissions> {
  label: string
  propName: keyof T
  disabled?: boolean
}

export interface Section<T extends server.dto.InternalEnvironmentPermissions | server.dto.ExternalEnvironmentPermissions> {
  label: string
  isVisible: boolean
  permissions: Array<Permission<T>>
}

export interface DocProps {
  access?: InternalPermProp
  modify?: InternalPermProp
  delete?: InternalPermProp
  deleteMerge?: InternalPermProp
  export?: InternalPermProp
  modifyPayments?: InternalPermProp
}

const getDefaultSection = (label: string, isVisible: boolean, permissions: server.dto.InternalEnvironmentPermissions, props: DocProps): Section<server.dto.InternalEnvironmentPermissions> => {
  const section: Section<server.dto.InternalEnvironmentPermissions> = {
    label,
    isVisible,
    permissions: []
  };

  const alwaysHasAccess = props.access == null;

  if (props.access) {
    section.permissions.push({ label: RESX.SoUser.Access, propName: props.access });
  }
  if (props.modify) {
    section.permissions.push({ label: RESX.SoUser.Modify, propName: props.modify, disabled: !alwaysHasAccess && !permissions[props.access] });
  }
  if (props.delete) {
    section.permissions.push({ label: RESX.Buttons.btnDelete, propName: props.delete, disabled: !permissions[props.modify] });
  }
  if (props.deleteMerge) {
    section.permissions.push({ label: `${RESX.Buttons.btnDelete} / ${RESX.OptionsPanel.Merge}`, propName: props.deleteMerge, disabled: !permissions[props.modify] });
  }
  if (props.export) {
    section.permissions.push({ label: RESX.SoUser.ListExport, propName: props.export, disabled: !alwaysHasAccess && !permissions[props.access] });
  }
  if (props.modifyPayments) {
    section.permissions.push({ label: RESX.Payment.ModifyPayments, propName: props.modifyPayments, disabled: !alwaysHasAccess && !permissions[props.access] });
  }

  return section;
};

export const getInternalSections = (config: server.dto.ClientConfiguration, permissions: server.dto.InternalEnvironmentPermissions): Array<Section<server.dto.InternalEnvironmentPermissions>> => [{
  label: RESX.GeneralLabels.General,
  isVisible: true,
  permissions: [{
    label: RESX.Template.TemplateSettings,
    propName: "configureTemplates",
    disabled: permissions.configureSystemSettings
  }, {
    label: RESX.GeneralLabels.SystemSettings,
    propName: "configureSystemSettings"
  }, {
    label: RESX.DocumentLifeCycle.Review,
    propName: "review"
  }, {
    label: `${RESX.InvoiceExport.Lock} / ${RESX.InvoiceExport.Unlock}`,
    propName: "lockUnlockItems"
  }]
}, {
  label: RESX.Financial.FinancialDetails,
  isVisible: true,
  permissions: [{
    label: RESX.Financial.Costs,
    propName: "readCosts"
  }, {
    label: RESX.Financial.Sales,
    propName: "readSales"
  }, {
    label: RESX.Financial.Totals,
    propName: "readTotals",
    disabled: !permissions.readCosts && !permissions.readSales
  }]
},
{ ...getDefaultSection(RESX.Address.AddressBook, true, permissions, { modify: "modifyInAddressBook", deleteMerge: "deleteInAddressBook", export: "exportInAddressBook" }) },
{ ...getDefaultSection(RESX.Planning.resxPlanning, true, permissions, { modify: "modifyInPlanning", delete: "deleteInPlanning", export: "exportInPlanning" }) },
{ ...getDefaultSection(config.quote.singular, config.quote.enabled, permissions, { access: "accessQuotes", modify: "modifyQuotes", delete: "deleteQuotes", export: "exportQuotes" }) },
{ ...getDefaultSection(config.purchaseAgreement.singular, config.purchaseAgreement.enabled, permissions, { access: "accessPurchaseAgreements", modify: "modifyPurchaseAgreements", delete: "deletePurchaseAgreements", export: "exportPurchaseAgreements" }) },
{ ...getDefaultSection(config.contract.singular, config.contract.enabled, permissions, { access: "accessContracts", modify: "modifyContracts", delete: "deleteContracts", export: "exportContracts" }) },
{ ...getDefaultSection(config.invoice.singular, config.invoice.enabled !== "none", permissions, { access: "accessInvoices", modify: "modifyInvoices", delete: "deleteInvoices", export: "exportInvoices", modifyPayments: "modifyPayments" }) },
{
  label: RESX.GeneralLabels.WebDevelopment,
  isVisible: true,
  permissions: [{
    label: RESX.Webform.Webforms,
    propName: "configureBookingForms"
  },
  {
    label: RESX.EmbedWizard.EmbedShows,
    propName: "configureEmbedShows"
  }]
}

];

export const getExternalPermissions = (config: server.dto.ClientConfiguration): Array<Section<server.dto.ExternalEnvironmentPermissions>> => [
  {
    label: RESX.Planning.resxPlanning,
    isVisible: true,
    permissions: [{
      label: RESX.SoUser.AddNewShows,
      propName: "addShows"
    }, {
      label: "Modify timetable",
      propName: "modifyTimetables"
    }, {
      label: RESX.SoUser.ReadPromoterDetails,
      propName: "readPromoterDetails"
    }, {
      label: RESX.SoUser.ReadTags,
      propName: "readTags"
    }, {
      label: RESX.SoUser.AccessTasks,
      propName: "accessTasks"
    }, {
      label: RESX.SoUser.AccessNotes,
      propName: "accessNotes"
    }, {
      label: RESX.SoUser.AccessEmails,
      propName: "accessEmails"
    }, {
      label: RESX.SoUser.AccessFiles,
      propName: "accessFiles"
    }, {
      label: RESX.SoUser.AccessGuestlist,
      propName: "accessGuestlists"
    }, {
      label: RESX.SoUser.ListExport,
      propName: "exportInPlanning"
    }].filter((p): p is Permission<server.dto.ExternalEnvironmentPermissions> =>
      p.propName !== "modifyTimetables" || config.hasModifyTimetablesPermission)
  },
  {
    label: RESX.Financial.FinancialDetails,
    isVisible: true,
    permissions: [{
      label: RESX.Financial.Costs,
      propName: "readCosts"
    }, {
      label: RESX.Financial.Sales,
      propName: "readSales"
    }]
  },
  {
    label: RESX.GeneralLabels.WebDevelopment,
    isVisible: true,
    permissions: [{
      label: RESX.Webform.Webforms,
      propName: "configureBookingForms"
    },
    {
      label: RESX.EmbedWizard.EmbedShows,
      propName: "configureEmbedShows"
    }
    ]
  }
];
